/**
 * AvatarUpload — small reusable widget that lets a signed-in user upload a
 * profile picture. Uploads to /api/images/upload (OBS-backed), then PATCHes
 * the returned URL onto User.avatarUrl via useAuth().updateProfile.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { resolveApiBaseUrl } from '../../utils/apiClient';

interface AvatarUploadProps {
  /** Optional callback after a successful upload + save, with the new URL. */
  onUpdated?: (newUrl: string | null) => void;
  /** Pixel diameter of the avatar circle. Default 96. */
  size?: number;
  /** Show a "Remove" link to clear the avatar. Default true. */
  allowRemove?: boolean;
}

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

export default function AvatarUpload({ onUpdated, size = 96, allowRemove = true }: AvatarUploadProps) {
  const { t, i18n } = useTranslation('common');
  const isRTL = i18n.language === 'ar';
  const { user, session, updateProfile } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentUrl = user?.avatarUrl ?? null;
  const initial = (user?.firstName || user?.email || '?').charAt(0).toUpperCase();

  const handleFile = async (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError(isRTL ? 'يجب أن يكون الملف صورة' : 'File must be an image');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(isRTL ? 'الحد الأقصى لحجم الصورة 5 ميجابايت' : 'Image must be 5 MB or smaller');
      return;
    }
    if (!session?.token) {
      setError(isRTL ? 'يجب تسجيل الدخول' : 'You must be signed in');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${resolveApiBaseUrl()}/images/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.token}` },
        body: formData,
      });
      if (!res.ok) {
        throw new Error(`Upload failed (${res.status})`);
      }
      const data = await res.json();
      const url = data.secure_url || data.url;
      if (!url) {
        throw new Error('No URL returned by upload endpoint');
      }
      await updateProfile({ avatarUrl: url });
      onUpdated?.(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    setUploading(true);
    try {
      await updateProfile({ avatarUrl: null });
      onUpdated?.(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove avatar';
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div
        className="relative rounded-full overflow-hidden ring-2 ring-[#F2F8F7] shadow-sm shrink-0 bg-[#004D40] flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        {currentUrl ? (
          <img src={currentUrl} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <span className="text-white font-bold" style={{ fontSize: size * 0.4 }}>
            {initial}
          </span>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <i className="ri-loader-4-line text-white text-2xl animate-spin" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="px-3 py-1.5 text-sm rounded-lg bg-[#004D40] text-white hover:bg-[#003930] transition-colors disabled:opacity-50 disabled:cursor-wait"
          >
            <i className="ri-upload-line me-1" />
            {currentUrl
              ? (isRTL ? 'تغيير الصورة' : 'Change photo')
              : (isRTL ? 'إضافة صورة' : 'Add photo')}
          </button>
          {currentUrl && allowRemove && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={uploading}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              {t('remove', 'Remove')}
            </button>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-1.5">
          {isRTL ? 'JPG أو PNG، حد أقصى 5 ميجابايت' : 'JPG or PNG, up to 5 MB'}
        </p>
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
    </div>
  );
}
